import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useAuth } from "../../../auth/store";
import { ErrorState, Skeleton } from "../../../components/States";
import { CopyButton, PageHeader } from "../../../components/ui";
import { backendApi, backendKeys, type LogLine, type TraceSpan } from "../api";
import { LevelBadge, StatusBadge, ms, replayLink, when } from "../components";

interface Row {
  span: TraceSpan;
  depth: number;
}

/** Spans in call order, each under its parent. Orphans (parent not stored) start a new root. */
export function orderSpans(spans: TraceSpan[]): Row[] {
  const ids = new Set(spans.map((s) => s.spanId));
  const children = new Map<string, TraceSpan[]>();
  const roots: TraceSpan[] = [];
  for (const s of spans) {
    if (s.parentSpanId && ids.has(s.parentSpanId) && s.parentSpanId !== s.spanId) {
      const list = children.get(s.parentSpanId) ?? [];
      list.push(s);
      children.set(s.parentSpanId, list);
    } else roots.push(s);
  }
  const byStart = (a: TraceSpan, b: TraceSpan) => a.startMs - b.startMs;
  const out: Row[] = [];
  const seen = new Set<string>();
  const walk = (s: TraceSpan, depth: number) => {
    if (seen.has(s.spanId)) return;
    seen.add(s.spanId);
    out.push({ span: s, depth });
    for (const c of (children.get(s.spanId) ?? []).sort(byStart)) walk(c, depth + 1);
  };
  for (const r of roots.sort(byStart)) walk(r, 0);
  return out;
}

const HIDDEN_ATTRS = new Set(["backstory.events"]);

function spanLabel(s: TraceSpan): string {
  return s.attributes["db.statement"] ? s.attributes["db.statement"] : s.name;
}

export function TraceDetailPage() {
  const { traceId } = useParams({ strict: false }) as { traceId: string };
  const auth = useAuth();
  const q = useQuery({ queryKey: backendKeys.trace(auth.currentProjectId, traceId), queryFn: () => backendApi.trace(traceId), enabled: !!auth.currentProjectId });
  const [picked, setPicked] = useState<string | null>(null);
  const rows = useMemo(() => orderSpans(q.data?.trace.spans ?? []), [q.data]);
  const t = q.data?.trace;
  const root = rows[0]?.span;
  // Open on where the failure started: the step that threw, else the deepest failing step.
  const failing = rows.filter((r) => r.span.status === "error");
  const origin = failing.find((r) => r.span.attributes["exception.stacktrace"]) ?? [...failing].sort((a, b) => b.depth - a.depth)[0];
  const selected = rows.find((r) => r.span.spanId === picked)?.span ?? origin?.span ?? root;
  const total = Math.max(t?.durationMs ?? 0, 0.001);
  const errors = rows.filter((r) => r.span.status === "error").length;

  const back = (
    <Link to={"/backend" as never} className="btn ghost icon-btn" aria-label="Back to requests" title="Back to requests">
      ←
    </Link>
  );

  if (q.isPending) return (<><PageHeader title="Request" back={back} /><Skeleton /></>);
  if (q.isError || !t) return (<><PageHeader title="Request" back={back} /><div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div></>);

  const status = root?.attributes["http.response.status_code"];
  return (
    <>
      <PageHeader
        title={t.rootName}
        back={back}
        subtitle={<>{q.data.services.join(", ")} · {ms(t.durationMs)} · {when(t.startMs)}{errors ? ` · ${errors} error${errors === 1 ? "" : "s"}` : ""}</>}
        actions={
          <>
            <CopyButton value={t.traceId} label="Copy trace ID" />
            {q.data.sessionId && <Link {...replayLink(q.data.sessionId, t.startMs)} className="btn primary">Watch replay</Link>}
          </>
        }
      />

      <div className="card be-waterfall" role="list" aria-label="Steps in this request">
        <div className="be-wf-head" aria-hidden="true">
          <span>Step</span>
          <span className="be-wf-scale"><span>0</span><span>{ms(total / 2)}</span><span>{ms(total)}</span></span>
        </div>
        {rows.map(({ span, depth }) => {
          const left = ((span.startMs - t.startMs) / total) * 100;
          const width = Math.max((span.durationMs / total) * 100, 0.4);
          const active = selected?.spanId === span.spanId;
          return (
            <button
              type="button"
              role="listitem"
              key={span.spanId}
              className={`be-wf-row${active ? " active" : ""}${span.status === "error" ? " error" : ""}`}
              onClick={() => setPicked(span.spanId)}
              aria-current={active || undefined}
            >
              <span className="be-wf-name" style={{ paddingLeft: depth * 14 }} title={spanLabel(span)}>
                <span className="be-wf-service">{span.service}</span>
                <span className="be-wf-label">{spanLabel(span)}</span>
              </span>
              <span className="be-wf-track">
                <span className="be-wf-lane">
                  <span className="be-wf-bar" style={{ left: `${Math.min(left, 99.6)}%`, width: `${Math.min(width, 100 - Math.min(left, 99.6))}%` }} />
                </span>
                <span className="be-wf-dur mono">{ms(span.durationMs)}</span>
              </span>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="be-detail-grid">
          <section className="card section" aria-label="Step details">
            <h2 className="be-h2">{selected.name}</h2>
            <p className="muted small be-meta">
              {selected.service} · {selected.kind} · {ms(selected.durationMs)} · <StatusBadge status={selected.status} httpStatus={selected.spanId === root?.spanId && status ? Number(status) : undefined} />
            </p>
            {selected.attributes["exception.stacktrace"] && (
              <details open className="be-stack">
                <summary>{selected.attributes["exception.type"] ?? "Error"}: {selected.attributes["exception.message"] ?? ""}</summary>
                <pre>{selected.attributes["exception.stacktrace"]}</pre>
              </details>
            )}
            <dl className="be-attrs">
              {Object.entries(selected.attributes)
                .filter(([k]) => !HIDDEN_ATTRS.has(k) && k !== "exception.stacktrace")
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="mono">{k}</dt>
                    <dd className="mono">{v}</dd>
                  </div>
                ))}
            </dl>
          </section>
          <section className="card section" aria-label="Logs from this request">
            <h2 className="be-h2">Logs</h2>
            <TraceLogs logs={q.data.logs} startMs={t.startMs} selected={selected.spanId} onPick={setPicked} />
          </section>
        </div>
      )}
    </>
  );
}

function TraceLogs({ logs, startMs, selected, onPick }: { logs: LogLine[]; startMs: number; selected: string; onPick: (id: string) => void }) {
  if (logs.length === 0) return <p className="muted small">No logs were written during this request.</p>;
  return (
    <ul className="be-trace-logs">
      {logs.map((l, i) => (
        <li key={`${l.ts}-${i}`} className={l.spanId === selected ? "active" : undefined}>
          <button type="button" onClick={() => l.spanId && onPick(l.spanId)} disabled={!l.spanId}>
            <span className="mono small muted">+{ms(Math.max(0, l.ts - startMs))}</span>
            <LevelBadge severity={l.severityNumber} />
            <span className="be-log-body">{l.body}</span>
          </button>
          {l.exceptionStack && (
            <details className="be-stack">
              <summary>{l.exceptionType || "Stack"}</summary>
              <pre>{l.exceptionStack}</pre>
            </details>
          )}
        </li>
      ))}
    </ul>
  );
}
